import { ReactNode, useCallback, useMemo, useState } from "react";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useTranslation } from "@coworker/locales";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import InputWrapper from "../InputWrapper/InputWrapper";
import ListItem from "../TaskCreatorAndEditorSheet/ListItem/ListItem";
import ErrorAddOn from "../TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import styles from "./text-modal.module.css";

type TextModalProps = {
  title: string;
  text: string;
  onConfirmClick: (newText: string) => void;
  disabled: boolean;
  maxLength: number;

  inputLabel?: string;
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>["inputMode"];
  numericOnly?: boolean;
  // Two different kinds of validation depending on use case.
  validate?: (value: string) => boolean;
  validateOnConfirm?: (value: string) => boolean;
  validationErrorText?: string;
  inputHintText?: string;
  errorMessage?: string;
  informationTitle?: string;
  informationBody?: ReactNode;
};

function TextModal({
  title,
  text,
  onConfirmClick,
  disabled,
  maxLength,
  inputLabel,
  inputMode,
  numericOnly,
  validate,
  validateOnConfirm,
  validationErrorText,
  inputHintText,
  errorMessage,
  informationTitle,
  informationBody,
}: TextModalProps) {
  const [showModal, setShowModal] = useState(false);
  const [newText, setNewText] = useState<string>(text);
  const [showError, setShowError] = useState(false);

  const { t } = useTranslation();

  const handleOpenModal = useCallback(() => {
    setNewText(text);
    setShowError(false);
    setShowModal(true);
  }, [text]);

  const {
    saveGuardedFunction: handleCloseModal,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowModal(false);
    }, [])
  );

  const maxLengthError = useMemo(() => {
    if (newText.length > maxLength) {
      return t("maximumCharactersLengthString", { count: maxLength });
    }
    return null;
  }, [newText, maxLength, t]);

  const handleConfirm = useCallback(() => {
    if (validateOnConfirm && !validateOnConfirm(newText)) {
      setShowError(true);
      return;
    }
    if (maxLengthError) return;
    onConfirmClick(newText);
    setShowModal(false);
    disableSaveGuard();
  }, [
    validateOnConfirm,
    newText,
    maxLengthError,
    onConfirmClick,
    disableSaveGuard,
  ]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setNewText(numericOnly ? raw.replace(/\D/g, "") : raw);
      setShowError(false);
      enableSaveGuard();
    },
    [numericOnly, enableSaveGuard]
  );

  const validationError =
    showError && validationErrorText ? validationErrorText : null;
  const displayError = maxLengthError ?? validationError;

  const confirmDisabled = validate ? !validate(newText) : false;

  return (
    <>
      <ListItem
        title={title}
        onClick={handleOpenModal}
        emphasised={!!text}
        description={text}
        disabled={disabled}
        addOn={errorMessage ? <ErrorAddOn>{errorMessage}</ErrorAddOn> : null}
      />
      <FixaModal
        visible={showModal}
        handleCloseBtn={handleCloseModal}
        data-testid="TextModal-FixaModal"
      >
        <FixaSheets
          labelledById="task-management-text-modal-header"
          header={
            <FixaModalHeader
              id="task-management-text-modal-header"
              title={title}
              closeBtnClick={handleCloseModal}
            />
          }
          footer={
            <FixaModalFooter renderBorder>
              <FixaButton
                type="primary"
                fluid
                text={t("confirmString")}
                onClick={handleConfirm}
                disabled={disabled || confirmDisabled}
              />
            </FixaModalFooter>
          }
          fullSize
        >
          <FixaModalBody>
            <InputWrapper error={displayError}>
              <FixaInputField
                type="text"
                id="text"
                label={inputLabel}
                inputMode={inputMode}
                value={newText}
                onChange={handleChange}
                maxLength={maxLength}
                autoFocus
              />
            </InputWrapper>
            {inputHintText && (
              <FixaText tagName="p" textStyle="Body.S">
                {inputHintText}
              </FixaText>
            )}
            {informationTitle && (
              <div className={styles["information-section"]}>
                <FixaText
                  tagName="p"
                  textStyle="Heading.XS"
                  className={styles["information-section-title"]}
                >
                  {informationTitle}
                </FixaText>
                {informationBody && (
                  <div className={styles["information-section-body"]}>
                    {informationBody}
                  </div>
                )}
              </div>
            )}
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </>
  );
}

export default TextModal;
